import type { JobPosition } from '../types/User.ts'

type LegacyJobPosition = JobPosition & {
    location?: unknown
    description?: unknown
}

function toTrimmedString(value: unknown): string {
    return typeof value === 'string' ? value.trim() : ''
}

const NAMED_HTML_ENTITIES: Record<string, string> = {
    nbsp: ' ',
    amp: '&',
    lt: '<',
    gt: '>',
    quot: '"',
    apos: "'"
}

function fromCodePointSafe(codePoint: number): string {
    if (!Number.isInteger(codePoint) || codePoint < 0 || codePoint > 0x10ffff) {
        return ''
    }

    try {
        return String.fromCodePoint(codePoint)
    } catch {
        return ''
    }
}

function decodeHtmlEntities(value: string): string {
    return value
        .replace(/&#x([0-9a-f]+);/gi, (_, hex: string) => fromCodePointSafe(Number.parseInt(hex, 16)))
        .replace(/&#(\d+);/g, (_, dec: string) => fromCodePointSafe(Number.parseInt(dec, 10)))
        .replace(/&([a-z]+);/gi, (match: string, name: string) => NAMED_HTML_ENTITIES[name.toLowerCase()] ?? match)
}

function isLinkedInWysiwygHtml(value: string): boolean {
    return /<div[^>]*class=(['"])[^'"]*\bwysiwyg-text\b[^'"]*\1[^>]*>/i.test(value)
}

function normalizeHtmlDescription(value: string): string {
    if (!isLinkedInWysiwygHtml(value)) return value

    const text = value
        .replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, '')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>\s*<p[^>]*>/gi, '\n\n')
        .replace(/<\/li>\s*<li[^>]*>/gi, '\n- ')
        .replace(/<li[^>]*>/gi, '- ')
        .replace(/<\/(p|div|li|ul|ol|h[1-6]|blockquote|section|article)>/gi, '\n')
        .replace(/<[^>]+>/g, '')

    return decodeHtmlEntities(text)
        .replace(/\r\n/g, '\n')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n[ \t]+/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim()
}

export function getProfileJobLocation(job: JobPosition): string {
    const legacyJob = job as LegacyJobPosition
    return toTrimmedString(legacyJob.location) || toTrimmedString(job.address)
}

export function getProfileJobDescription(job: JobPosition): string {
    const legacyJob = job as LegacyJobPosition
    const rawDescription = toTrimmedString(legacyJob.description) || toTrimmedString(job.desc)
    return normalizeHtmlDescription(rawDescription)
}
